import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import {
  useStoresQuery,
  useCreateStoreMutation,
  useUpdateStoreMutation,
  useDeleteStoreMutation
} from "@queries/store";
import api from "@services/api";
import "@styles/opencart-admin.css";

const defaultForm = {
  name: "",
  url: "",
  ssl: "",
  is_marketplace_default: 0,

  config_geocode: "",
  config_country_id: "",
  config_city_id: "",
  config_language: "",
  config_currency: "",
  config_franchise_id: "",
  config_url: "",
  config_ssl: "",
  config_meta_title: "",
  config_meta_description: "",
  config_meta_keyword: "",
  config_theme: "",
  config_layout_id: "",
  config_name: "",
  config_owner: "",
  config_address: "",
  config_email: "",
  config_telephone: "",
  config_fax: "",
  config_image: "",
  config_open: "",
  config_comment: "",
  config_tax_factor: "",
  config_tax: "",
  config_tax_default: "",
  config_tax_customer: "",
  config_customer_group_id: "",
  config_customer_price: "",
  config_account_id: "",
  config_cart_weight: "",
  config_checkout_guest: "",
  config_checkout_id: "",
  config_order_status_id: "",
  config_stock_display: "",
  config_stock_checkout: "",
  config_logo: "",
  config_icon: "",
  config_secure: ""
};

export default function StoresPage() {
  const [page, setPage] = React.useState(1);
  const pageSize = 20;
  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(defaultForm);

  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, isError, error } = useStoresQuery(
    page,
    pageSize
  );

  const {
    data: franchisePayload,
    isLoading: isFranchisesLoading
  } = useQuery({
    queryKey: ["ww-franchise", "all"],
    queryFn: async () => {
      const { data } = await api.get("/franchise");
      return data;
    }
  });

  const franchiseOptions = franchisePayload?.data || [];

  const createMutation = useCreateStoreMutation();
  const updateMutation = useUpdateStoreMutation();
  const deleteMutation = useDeleteStoreMutation();

  const rows = data?.data || [];
  const total = data?.meta?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  function resetForm() {
    setEditingId(null);
    setForm(defaultForm);
  }

  function getFranchiseName(id) {
    if (!id) return "";
    const item = franchiseOptions.find(
      (f) => String(f.franchise_id ?? f.id) === String(id)
    );
    return item?.name || "";
  }

  function extractSettings(source) {
    if (!source) return {};
    if (Array.isArray(source)) {
      return source.reduce((acc, row) => {
        if (row.key && row.value != null) {
          acc[row.key] = row.value;
        }
        return acc;
      }, {});
    }
    return source;
  }

  function mapStoreResponseToForm(payload) {
    const store = payload.store ?? payload;
    const settingsRaw = payload.settings ?? {};
    const settings = extractSettings(settingsRaw);

    function pick(key, fallback = "") {
      return (
        payload[key] ??
        settings[key] ??
        store[key] ??
        fallback
      );
    }

    return {
      name: store?.name || "",
      url: store?.url || "",
      ssl: store?.ssl || "",
      is_marketplace_default: store?.is_marketplace_default ? 1 : 0,

      config_geocode: pick("config_geocode", ""),
      config_country_id: pick("config_country_id", ""),
      config_city_id: pick("config_city_id", ""),
      config_language: pick("config_language", ""),
      config_currency: pick("config_currency", ""),
      config_franchise_id: pick("config_franchise_id", ""),
      config_url: pick("config_url", ""),
      config_ssl: pick("config_ssl", ""),
      config_meta_title: pick("config_meta_title", ""),
      config_meta_description: pick("config_meta_description", ""),
      config_meta_keyword: pick("config_meta_keyword", ""),
      config_theme: pick("config_theme", ""),
      config_layout_id: pick("config_layout_id", ""),
      config_name: pick("config_name", store?.name || ""),
      config_owner: pick("config_owner", ""),
      config_address: pick("config_address", ""),
      config_email: pick("config_email", ""),
      config_telephone: pick("config_telephone", ""),
      config_fax: pick("config_fax", ""),
      config_image: pick("config_image", ""),
      config_open: pick("config_open", ""),
      config_comment: pick("config_comment", ""),
      config_tax_factor: pick("config_tax_factor", ""),
      config_tax: pick("config_tax", ""),
      config_tax_default: pick("config_tax_default", ""),
      config_tax_customer: pick("config_tax_customer", ""),
      config_customer_group_id: pick("config_customer_group_id", ""),
      config_customer_price: pick("config_customer_price", ""),
      config_account_id: pick("config_account_id", ""),
      config_cart_weight: pick("config_cart_weight", ""),
      config_checkout_guest: pick("config_checkout_guest", ""),
      config_checkout_id: pick("config_checkout_id", ""),
      config_order_status_id: pick("config_order_status_id", ""),
      config_stock_display: pick("config_stock_display", ""),
      config_stock_checkout: pick("config_stock_checkout", ""),
      config_logo: pick("config_logo", ""),
      config_icon: pick("config_icon", ""),
      config_secure: pick("config_secure", "")
    };
  }

  async function openCreate() {
    resetForm();
  }

  async function openEdit(row) {
    const id = row.store_id ?? row.id;
    if (!id) return;
    const { data: payload } = await api.get(`/store/${id}`);
    const mapped = mapStoreResponseToForm(payload);
    setEditingId(id);
    setForm(mapped);
  }

  async function handleSave(e) {
    e.preventDefault();

    const payload = {
      name: form.name,
      url: form.url,
      ssl: form.ssl,
      is_marketplace_default: Number(form.is_marketplace_default) ? 1 : 0,

      config_geocode: form.config_geocode,
      config_country_id: form.config_country_id,
      config_city_id: form.config_city_id,
      config_language: form.config_language,
      config_currency: form.config_currency,
      config_franchise_id: form.config_franchise_id,
      config_url: form.config_url,
      config_ssl: form.config_ssl,
      config_meta_title: form.config_meta_title,
      config_meta_description: form.config_meta_description,
      config_meta_keyword: form.config_meta_keyword,
      config_theme: form.config_theme,
      config_layout_id: form.config_layout_id,
      config_name: form.config_name,
      config_owner: form.config_owner,
      config_address: form.config_address,
      config_email: form.config_email,
      config_telephone: form.config_telephone,
      config_fax: form.config_fax,
      config_image: form.config_image,
      config_open: form.config_open,
      config_comment: form.config_comment,
      config_tax_factor: form.config_tax_factor,
      config_tax: form.config_tax,
      config_tax_default: form.config_tax_default,
      config_tax_customer: form.config_tax_customer,
      config_customer_group_id: form.config_customer_group_id,
      config_customer_price: form.config_customer_price,
      config_account_id: form.config_account_id,
      config_cart_weight: form.config_cart_weight,
      config_checkout_guest: form.config_checkout_guest,
      config_checkout_id: form.config_checkout_id,
      config_order_status_id: form.config_order_status_id,
      config_stock_display: form.config_stock_display,
      config_stock_checkout: form.config_stock_checkout,
      config_logo: form.config_logo,
      config_icon: form.config_icon,
      config_secure: form.config_secure
    };

    if (!payload.name) {
      alert("Name is required.");
      return;
    }

    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, payload });
    } else {
      await createMutation.mutateAsync(payload);
    }

    resetForm();
    queryClient.invalidateQueries({ queryKey: ["oc-store"] });
  }

  async function handleDelete(row) {
    const id = row.store_id ?? row.id;
    if (!id) return;
    if (!window.confirm("Delete this store?")) return;
    await deleteMutation.mutateAsync(id);
    queryClient.invalidateQueries({ queryKey: ["oc-store"] });
  }

  return (
    <div className="oc-content">
      {(isLoading || isFetching || isFranchisesLoading) && <Loader />}

      <div className="oc-header">
        <h1>Stores</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Stores</span>
        </nav>
      </div>

      <div className="oc-card" style={{ marginBottom: 16 }}>
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>{editingId ? "Edit Store" : "Add Store"}</strong>
          </div>
          <div>
            <button
              type="button"
              className="oc-btn ghost"
              onClick={openCreate}
            >
              New
            </button>
          </div>
        </div>

        <form onSubmit={handleSave}>
          <div className="form-row">
            <div className="form-group">
              <label>Store name</label>
              <input
                className="oc-input"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                required
              />
            </div>
            <div className="form-group">
              <label>Franchise</label>
              <select
                className="oc-input"
                value={form.config_franchise_id}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_franchise_id: e.target.value
                  }))
                }
              >
                <option value="">Select franchise</option>
                {franchiseOptions.map((fr) => (
                  <option
                    key={fr.franchise_id ?? fr.id}
                    value={fr.franchise_id ?? fr.id}
                  >
                    {fr.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Tax factor</label>
              <input
                type="number"
                step="0.01"
                className="oc-input"
                value={form.config_tax_factor}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_tax_factor: e.target.value
                  }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Store URL</label>
              <input
                className="oc-input"
                value={form.url}
                onChange={(e) =>
                  setForm((f) => ({ ...f, url: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>SSL URL</label>
              <input
                className="oc-input"
                value={form.ssl}
                onChange={(e) =>
                  setForm((f) => ({ ...f, ssl: e.target.value }))
                }
              />
            </div>
            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={Number(form.is_marketplace_default) === 1}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      is_marketplace_default: e.target.checked ? 1 : 0
                    }))
                  }
                />{" "}
                Marketplace default
              </label>
            </div>
          </div>

          <hr />

          <div className="form-row">
            <div className="form-group">
              <label>Config name</label>
              <input
                className="oc-input"
                value={form.config_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_name: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Owner</label>
              <input
                className="oc-input"
                value={form.config_owner}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_owner: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Address</label>
              <input
                className="oc-input"
                value={form.config_address}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_address: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input
                className="oc-input"
                value={form.config_email}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_email: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Telephone</label>
              <input
                className="oc-input"
                value={form.config_telephone}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_telephone: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>Fax</label>
              <input
                className="oc-input"
                value={form.config_fax}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_fax: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Geocode</label>
              <input
                className="oc-input"
                value={form.config_geocode}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_geocode: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Country ID</label>
              <input
                className="oc-input"
                value={form.config_country_id}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_country_id: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>City ID</label>
              <input
                className="oc-input"
                value={form.config_city_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_city_id: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Language code</label>
              <input
                className="oc-input"
                value={form.config_language}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_language: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Currency code</label>
              <input
                className="oc-input"
                value={form.config_currency}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_currency: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Meta title</label>
              <input
                className="oc-input"
                value={form.config_meta_title}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_meta_title: e.target.value
                  }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Meta description</label>
              <input
                className="oc-input"
                value={form.config_meta_description}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_meta_description: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>Meta keyword</label>
              <input
                className="oc-input"
                value={form.config_meta_keyword}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_meta_keyword: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>Theme</label>
              <input
                className="oc-input"
                value={form.config_theme}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_theme: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Layout ID</label>
              <input
                className="oc-input"
                value={form.config_layout_id}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_layout_id: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>Config URL</label>
              <input
                className="oc-input"
                value={form.config_url}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_url: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Config SSL</label>
              <input
                className="oc-input"
                value={form.config_ssl}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_ssl: e.target.value }))
                }
              />
            </div>
          </div>

          <hr />

          <div className="form-row">
            <div className="form-group">
              <label>Open hours</label>
              <input
                className="oc-input"
                value={form.config_open}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_open: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Comment</label>
              <input
                className="oc-input"
                value={form.config_comment}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_comment: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Secure (0/1)</label>
              <input
                className="oc-input"
                value={form.config_secure}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_secure: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Tax enabled (0/1)</label>
              <input
                className="oc-input"
                value={form.config_tax}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_tax: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Tax default</label>
              <input
                className="oc-input"
                value={form.config_tax_default}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_tax_default: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>Tax customer</label>
              <input
                className="oc-input"
                value={form.config_tax_customer}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_tax_customer: e.target.value
                  }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Customer group ID</label>
              <input
                className="oc-input"
                value={form.config_customer_group_id}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_customer_group_id: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>Customer price (0/1)</label>
              <input
                className="oc-input"
                value={form.config_customer_price}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_customer_price: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>Account page ID</label>
              <input
                className="oc-input"
                value={form.config_account_id}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_account_id: e.target.value
                  }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Cart weight (0/1)</label>
              <input
                className="oc-input"
                value={form.config_cart_weight}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_cart_weight: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>Checkout guest (0/1)</label>
              <input
                className="oc-input"
                value={form.config_checkout_guest}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_checkout_guest: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>Checkout page ID</label>
              <input
                className="oc-input"
                value={form.config_checkout_id}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_checkout_id: e.target.value
                  }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Order status ID</label>
              <input
                className="oc-input"
                value={form.config_order_status_id}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_order_status_id: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>Stock display (0/1)</label>
              <input
                className="oc-input"
                value={form.config_stock_display}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_stock_display: e.target.value
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label>Stock checkout (0/1)</label>
              <input
                className="oc-input"
                value={form.config_stock_checkout}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    config_stock_checkout: e.target.value
                  }))
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Logo path</label>
              <input
                className="oc-input"
                value={form.config_logo}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_logo: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Icon path</label>
              <input
                className="oc-input"
                value={form.config_icon}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_icon: e.target.value }))
                }
              />
            </div>
            <div className="form-group">
              <label>Image path</label>
              <input
                className="oc-input"
                value={form.config_image}
                onChange={(e) =>
                  setForm((f) => ({ ...f, config_image: e.target.value }))
                }
              />
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "flex-end",
              gap: 8
            }}
          >
            <button
              type="button"
              className="oc-btn ghost"
              onClick={resetForm}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="oc-btn primary"
              disabled={
                createMutation.isLoading || updateMutation.isLoading
              }
            >
              Save
            </button>
          </div>
        </form>
      </div>

      <div className="oc-card">
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>Stores</strong>
          </div>
        </div>

        {isError && (
          <div style={{ color: "#c00", marginBottom: 8 }}>
            Error: {error?.message || "Unknown error"}
          </div>
        )}

        <table className="oc-table">
          <thead>
            <tr>
              <th style={{ width: 70 }}>ID</th>
              <th>Name</th>
              <th>Franchise</th>
              <th style={{ width: 100 }}>Tax factor</th>
              <th>URL</th>
              <th style={{ width: 140 }}>Marketplace default</th>
              <th style={{ width: 160 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 24 }}>
                  No stores
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const id = row.store_id ?? row.id;
              const settings = extractSettings(row.settings);
              const franchiseId =
                row.config_franchise_id ??
                settings.config_franchise_id ??
                row.franchise_id;
              const taxFactor =
                row.config_tax_factor ??
                settings.config_tax_factor ??
                "";
              return (
                <tr key={id}>
                  <td>{id}</td>
                  <td>{row.name}</td>
                  <td>{getFranchiseName(franchiseId)}</td>
                  <td>{taxFactor}</td>
                  <td>{row.url}</td>
                  <td>
                    <span
                      className={
                        row.is_marketplace_default
                          ? "oc-badge yes"
                          : "oc-badge no"
                      }
                    >
                      {row.is_marketplace_default ? "Yes" : "No"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        className="oc-btn blue"
                        onClick={() => openEdit(row)}
                      >
                        edit
                      </button>
                      <button
                        className="oc-btn danger"
                        onClick={() => handleDelete(row)}
                      >
                        delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        className="oc-card"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 16
        }}
      >
        <div>
          Showing page <strong>{page}</strong> of <strong>{pages}</strong> (
          {total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage(1)}
          >
            « First
          </button>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ‹ Prev
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next ›
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage(pages)}
          >
            Last »
          </button>
        </div>
      </div>
    </div>
  );
}

